import { AxiosHelper } from '@/modules/shared/infra/protocols/http';
import { useSurveyForm } from '@/modules/SurveyForm';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { RemoteSelectDependsOn } from './interfaces';

interface ValidateResponseItem {
  id: string;
  name: string;
}

interface ValidateResponse {
  items: ValidateResponseItem[];
}

function normalizeToIds(value: any): string[] {
  if (value === undefined || value === null) return [];
  if (Array.isArray(value)) {
    return value
      .map((v) => (v && typeof v === 'object' ? v.id : v))
      .filter((id) => id !== undefined && id !== null)
      .map(String);
  }
  if (typeof value === 'object') return value.id ? [String(value.id)] : [];
  return [String(value)];
}

interface UseDependentOptionsUrlParams {
  questionId: string;
  optionsUrl?: string;
  dependsOn?: RemoteSelectDependsOn[];
  validateByParam?: string;
  isMulti: boolean;
  currentValues: any;
  onPrune: (newValues: any) => void;
}

interface UseDependentOptionsUrlResult {
  dependentUrl?: string;
  remountKey: string;
}

/**
 * Torna um remote_single_select/remote_multi_select um filtro dependente de
 * outra(s) pergunta(s) do formulário (question.config.dependsOn). Sempre que
 * uma pergunta pai mudar, o optionsUrl é recomposto com os params derivados do
 * valor do pai, e, se `validateByParam` estiver configurado, a seleção atual é
 * validada contra o novo filtro: ids que deixaram de ser válidos são removidos
 * (prune), mantendo os que continuam válidos.
 */
export function useDependentOptionsUrl({
  questionId,
  optionsUrl,
  dependsOn,
  validateByParam,
  isMulti,
  currentValues,
  onPrune,
}: UseDependentOptionsUrlParams): UseDependentOptionsUrlResult {
  const { getValues, subscribe } = useSurveyForm();

  const deps = useMemo(() => dependsOn || [], [dependsOn]);
  const isDependent = deps.length > 0 && !!optionsUrl;

  const currentValuesRef = useRef(currentValues);
  currentValuesRef.current = currentValues;

  const buildDependentQuery = useCallback(() => {
    const parts: string[] = [];
    deps.forEach((dep) => {
      const parentValue = getValues(dep.questionId)?.values;
      Object.entries(dep.params).forEach(([sourceKey, paramName]) => {
        const raw =
          parentValue && typeof parentValue === 'object'
            ? parentValue[sourceKey]
            : undefined;
        normalizeToIds(raw).forEach((id) => {
          parts.push(`${paramName}[]=${encodeURIComponent(id)}`);
        });
      });
    });
    return parts.join('&');
  }, [deps, getValues]);

  const [dependentQuery, setDependentQuery] = useState<string>(() =>
    isDependent ? buildDependentQuery() : '',
  );
  const [version, setVersion] = useState(0);

  const dependentUrl = useMemo(() => {
    if (!optionsUrl) return optionsUrl;
    if (!isDependent || !dependentQuery) return optionsUrl;
    return `${optionsUrl}&${dependentQuery}`;
  }, [optionsUrl, isDependent, dependentQuery]);

  const pruneSelection = useCallback(
    async (query: string) => {
      if (!validateByParam || !optionsUrl) return;

      const currentIds = normalizeToIds(currentValuesRef.current);
      if (!currentIds.length) return;

      const validateQs = currentIds
        .map((id) => `${validateByParam}=${encodeURIComponent(id)}`)
        .join('&');
      const url = `${optionsUrl}${query ? `&${query}` : ''}&${validateQs}`;

      try {
        const { body, statusCode } = await AxiosHelper.get<ValidateResponse>({
          url,
        });
        if (statusCode < 200 || statusCode >= 300) return;

        const validItems = body.items || [];
        const validIds = new Set(validItems.map((item) => String(item.id)));
        const stillValid = currentIds.filter((id) => validIds.has(id));

        if (stillValid.length === currentIds.length) return;

        if (!stillValid.length) {
          onPrune(isMulti ? [] : null);
        } else if (isMulti) {
          onPrune(stillValid.map((id) => ({ id })));
        } else {
          const survivor = validItems.find(
            (item) => String(item.id) === stillValid[0],
          );
          onPrune(survivor ? { id: survivor.id, name: survivor.name } : null);
        }

        // pruneSelection resolve de forma assíncrona, depois que o remount
        // disparado pelo refetch (que já aconteceu de forma síncrona) usou o
        // valor selecionado ainda antigo como defaultValue. Sem forçar um
        // novo remount aqui, o dropdown (uncontrolled) não reflete o prune
        // até a PRÓXIMA mudança de dependência.
        setVersion((v) => v + 1);
      } catch {
        // erro de rede na validação: mantém a seleção atual intacta
      }
    },
    [validateByParam, optionsUrl, isMulti, onPrune],
  );

  // Baseline persistida (imune a re-execuções do efeito abaixo, que pode
  // re-rodar por motivos alheios a uma mudança real do usuário — ex: a
  // referência de `dependsOn` mudando entre renders do form). Só decide
  // "isso é uma mudança real, faça o prune" quando a query computada diverge
  // do último valor efetivamente visto — nunca por contagem de chamadas.
  const baselineQueryRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isDependent) return undefined;

    const refetch = () => {
      const nextQuery = buildDependentQuery();
      setDependentQuery(nextQuery);
      setVersion((v) => v + 1);

      if (baselineQueryRef.current === nextQuery) {
        return;
      }
      const isFirstObservation = baselineQueryRef.current === null;
      baselineQueryRef.current = nextQuery;

      if (isFirstObservation) return;

      pruneSelection(nextQuery);
    };

    const unsubs = deps.map((dep) => subscribe(dep.questionId, refetch));
    return () => {
      unsubs.forEach((unsub) => unsub && unsub());
    };
  }, [isDependent, deps, subscribe, buildDependentQuery, pruneSelection]);

  const remountKey = `${questionId}:${version}`;

  return { dependentUrl, remountKey };
}
